import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Col, Form, Row } from "reactstrap";
import { useBoardCore } from "../../../../cores/boards";
import { useIntl } from "react-intl";
import { useMeCore } from "../../../../cores/me";
import { CardTag } from "../../../../cores/boards/interfaces/api/get-tags";
import { InputWembii } from "../../InputWembii";
import { useGlobalCore } from "../../../../cores/globals";
import { CARD_TAGS } from "../../../../constants/internalModal.constant";

interface Props {
  board: string;
  tag?: CardTag;
  onSuccess: () => void;
}

const DEFAULT_TEXT_COLOR = "#ffffff";
const DEFAULT_BACKGROUND_COLOR = "#0b65c2";

const normalizeTagColor = (value?: string, fallback = "#000000"): string => {
  const trimmed = (value || "").trim();
  if (!trimmed) return fallback;

  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  if (/^#[0-9A-Fa-f]{3}$/.test(withHash)) {
    const [, r, g, b] = withHash.match(/^#(.)(.)(.)$/)!;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  if (/^#[0-9A-Fa-f]{6}$/.test(withHash)) {
    return withHash.toLowerCase();
  }

  return fallback;
};

export default function TagForm({ tag, board, onSuccess }: Props) {
  const intl = useIntl();
  const { company } = useMeCore();
  const { turnOffInternalModal } = useGlobalCore();
  const { postCardTag, putCardTag, deleteCardTag } = useBoardCore();

  const [name, setName] = useState("");
  const [textColor, setTextColor] = useState(DEFAULT_TEXT_COLOR);
  const [backgroundColor, setBackgroundColor] = useState(DEFAULT_BACKGROUND_COLOR);

  useEffect(() => {
    if (tag) {
      setTextColor(normalizeTagColor(tag.textColor, DEFAULT_TEXT_COLOR));
      setBackgroundColor(normalizeTagColor(tag.backgroundColor, DEFAULT_BACKGROUND_COLOR));
      setName(tag.name);
      return;
    }

    setTextColor(DEFAULT_TEXT_COLOR);
    setBackgroundColor(DEFAULT_BACKGROUND_COLOR);
    setName("");
  }, [tag]);

  const cleanForm = useCallback(() => {
    setTextColor(DEFAULT_TEXT_COLOR);
    setBackgroundColor(DEFAULT_BACKGROUND_COLOR);
    setName("");
    onSuccess();
    turnOffInternalModal(CARD_TAGS);
  }, [turnOffInternalModal, onSuccess]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedTextColor = normalizeTagColor(textColor, DEFAULT_TEXT_COLOR);
    const normalizedBackgroundColor = normalizeTagColor(
      backgroundColor,
      DEFAULT_BACKGROUND_COLOR
    );

    if (!tag) {
      postCardTag(
        company?.id!,
        board,
        name,
        normalizedTextColor,
        normalizedBackgroundColor,
        cleanForm
      );
    } else {
      putCardTag(
        company?.id!,
        board,
        tag.id,
        name,
        normalizedTextColor,
        normalizedBackgroundColor,
        cleanForm
      );
    }
  };

  const onDelete = useCallback(() => {
    deleteCardTag(company?.id!, board, tag?.id!, cleanForm);
  }, [deleteCardTag, cleanForm, company, tag, board]);

  const disabled = useCallback(
    () => !name || !textColor || !backgroundColor,
    [name, textColor, backgroundColor]
  );

  return (
    <Form className="p-2 tag-form" onSubmit={onSubmit}>
      <Row>
        <Col xs="12" md="6">
          <InputWembii
            label={intl.formatMessage({ id: "app.activity.tags.textColor" })}
            type="color"
            name="textColor"
            id="tag-text-color"
            required
            placeholder={intl.formatMessage({ id: "app.activity.tags.textColor" })}
            value={textColor}
            onChange={setTextColor}
          />
          <InputWembii
            label={intl.formatMessage({ id: "app.activity.tags.backgroundColor" })}
            type="color"
            name="backgroundColor"
            id="tag-background-color"
            required
            placeholder={intl.formatMessage({
              id: "app.activity.tags.backgroundColor",
            })}
            value={backgroundColor}
            onChange={setBackgroundColor}
          />
        </Col>
        <Col xs="12" md="6">
          <InputWembii
            label={intl.formatMessage({ id: "app.activity.tags.name" })}
            type="text"
            name="name"
            id="tag-name"
            required
            placeholder={intl.formatMessage({ id: "app.activity.tags.name" })}
            value={name}
            onChange={setName}
          />
          {name && (
            <Badge
              pill
              className="my-2 tag-form__preview"
              style={{ backgroundColor }}
            >
              <div className="px-2 py-1">
                <span className="h6 mb-0 font-weight-bold" style={{ color: textColor }}>
                  {name}
                </span>
              </div>
            </Badge>
          )}
        </Col>
      </Row>

      {tag && (
        <Button color="danger" className="mr-1" onClick={onDelete}>
          {intl.formatMessage({ id: "app.activity.tags.delete" })}
        </Button>
      )}
      <Button color="primary" disabled={disabled()} className="ml-1">
        {tag
          ? intl.formatMessage({ id: "app.activity.tags.update" })
          : intl.formatMessage({ id: "app.activity.tags.create" })}
      </Button>
    </Form>
  );
}
